import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Auth0Provider, useAuth0, type AppState } from '@auth0/auth0-react';
import {
  AUTH0_AUDIENCE,
  AUTH0_CLIENT_ID,
  AUTH0_CONNECTION,
  AUTH0_DOMAIN,
  AUTH_MODE,
  type AuthMode,
} from '../env';
import { DEV_USERS, initialDevUser, rememberDevUser, type DevUser } from './dev-users';
import { BareState } from '../components/ui/bare-state';
import { Loading } from '../components/ui/loading';

/**
 * Auth is one thing to the rest of the app: an authed fetch (ui-design §6.3).
 *
 * Dev mode (`VITE_AUTH_MODE=dev`, local-dev.md §2.4) mounts no Auth0 provider
 * at all — the wrapper sends `x-dev-user: <email>` and the nav foot carries the
 * Lew ⇄ Soroush switcher. Auth0 mode is Authorization Code + PKCE with rotating
 * refresh tokens and a localstorage cache: an office tool where a hard refresh
 * must not bounce two named users to a login screen daily.
 *
 * Both modes hand the app the same shape, so nothing above this file knows
 * which one it is running under.
 */

export interface AuthState {
  mode: AuthMode;
  /** The one fetch every API call goes through. */
  fetchWithAuth: (input: string, init?: RequestInit) => Promise<Response>;
  /** Dev only: which of the two seeded users is calling. */
  devUser: DevUser | null;
  devUsers: readonly DevUser[];
  switchDevUser: (email: string) => void;
  /** Auth0 only; a no-op in dev, where there is no session to end. */
  signOut: (() => void) | null;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const state = useContext(AuthContext);
  if (!state) throw new Error('useAuth called outside AuthProvider');
  return state;
}

function DevAuthProvider({ children }: { children: ReactNode }) {
  const [email, setEmail] = useState<string>(() => initialDevUser());
  const queryClient = useQueryClient();

  const switchDevUser = useCallback(
    (next: string) => {
      rememberDevUser(next);
      setEmail(next);
      // Every cached read belongs to the user who made it; switching who the
      // server thinks is calling drops the cache rather than reusing it.
      queryClient.clear();
    },
    [queryClient],
  );

  const fetchWithAuth = useCallback(
    (input: string, init?: RequestInit) => {
      const headers = new Headers(init?.headers);
      headers.set('x-dev-user', email);
      return fetch(input, { ...init, headers });
    },
    [email],
  );

  const value = useMemo<AuthState>(
    () => ({
      mode: 'dev',
      fetchWithAuth,
      devUser: DEV_USERS.find((user) => user.email === email) ?? { email, name: email },
      devUsers: DEV_USERS,
      switchDevUser,
      signOut: null,
    }),
    [email, fetchWithAuth, switchDevUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/* ————————————————————————— Auth0 ————————————————————————— */

/**
 * Two facts that have to outlive the page, because both of the loops they
 * close run *through* a redirect:
 *
 *   `ni:relogin-attempted` — a 401 has already bounced this visit through
 *     Auth0 once. The server 401s perfectly valid sessions (no `app_user` row
 *     yet, an unverified email, a `sub` collision — server/src/auth/
 *     user-verifier.ts), and against a live Auth0 session `loginWithRedirect`
 *     returns immediately with the same identity and earns the same 401. Left
 *     ungated that is an infinite redirect, and it lands on exactly the
 *     bootstrap path the README documents: first sign-in, before the row exists.
 *
 *   `ni:signed-out` — the reader asked to leave. Auth0's logout returns to the
 *     origin, the app remounts, and the Google session is still live, so the
 *     auto-login effect would sign them straight back in.
 *
 * sessionStorage, not localStorage: both are about this tab's current visit and
 * neither should outlive it. Every access is wrapped — a browser with site data
 * blocked throws on the getter, and a blank screen is a worse answer than
 * losing the across-loads half of the guard.
 */
const RELOGIN_KEY = 'ni:relogin-attempted';
const SIGNED_OUT_KEY = 'ni:signed-out';

function flagIsSet(key: string): boolean {
  try {
    return window.sessionStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

function setFlag(key: string): void {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    // Storage refused. The in-page guards still hold for this load.
  }
}

function clearFlag(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // As above.
  }
}

/**
 * The one redirect target Terraform registers on the client:
 * `callbacks = [for o in var.spa_origins : "${o}/callback"]`
 * (`modules/auth0/spa.tf`). Anything else is a "Callback URL mismatch".
 */
const CALLBACK_PATH = '/callback';

/** Where sign-in should land the reader back. Never the callback itself. */
function currentPath(): string {
  const { pathname, search } = window.location;
  if (pathname === CALLBACK_PATH) return '/';
  return `${pathname}${search}`;
}

/**
 * The provider does the code exchange wherever it lands; this only puts the
 * address bar back where the reader was, before the router ever reads it.
 *
 * `history.replaceState` rather than a router navigate on purpose: the router
 * mounts *below* this provider and below the `GET /me` bootstrap (main.tsx), so
 * at the moment the exchange finishes there is no navigate to call. The router
 * then mounts on the restored path, which is the same outcome with one fewer
 * moving part. `/callback` is still a route (app.tsx) for the case where a
 * reader reloads that address with a live session and no code to exchange.
 */
function onRedirectCallback(appState?: AppState): void {
  const target = typeof appState?.returnTo === 'string' ? appState.returnTo : '/';
  window.history.replaceState({}, document.title, target);
}

/**
 * Inside the provider: the session, the authed fetch built on it, and the
 * automatic redirect. This is an office tool behind a login — there is no
 * marketing page to land on, so an unauthenticated reader is sent to Google
 * rather than shown a sign-in button.
 */
function Auth0Session({ children }: { children: ReactNode }) {
  const {
    isLoading,
    isAuthenticated,
    error,
    user,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
  } = useAuth0();

  /** A live session the API will not have. Terminal, with one way out. */
  const [noAccess, setNoAccess] = useState(false);
  /** Auth0 would not mint a token even after a fresh trip through sign-in. */
  const [tokenError, setTokenError] = useState<string | null>(null);
  /** Written before the logout redirect; read back on the load after it. */
  const [signedOut, setSignedOut] = useState(() => flagIsSet(SIGNED_OUT_KEY));

  // One redirect per page load. StrictMode double-invokes effects, a poll can
  // 401 several times in a row, and every one of those would otherwise start
  // its own authorize call while the first is still navigating away.
  const redirecting = useRef(false);
  /**
   * The page is on its way to Auth0. The app below is unmounted for the
   * moment the navigation takes, because the read that triggered it has just
   * been answered with a synthetic 401 and would otherwise render "the server
   * did not answer" on the way out.
   */
  const [leaving, setLeaving] = useState(false);

  const startLogin = useCallback(() => {
    if (redirecting.current) return;
    redirecting.current = true;
    setLeaving(true);
    loginWithRedirect({ appState: { returnTo: currentPath() } }).catch(() => {
      redirecting.current = false;
      setLeaving(false);
    });
  }, [loginWithRedirect]);

  /**
   * What a 401 — or a token that will not mint — actually means here.
   *
   * With a live session it is not a sign-in problem at all: the server knows
   * this identity and will not have it. Another trip through Auth0 returns the
   * same person and earns the same 401, so the redirect is skipped outright and
   * the reader is told what is wrong. Without a session one attempt is worth
   * making, and `RELOGIN_KEY` is what holds that "one" across the page load the
   * attempt causes.
   */
  const handleUnauthorized = useCallback(() => {
    if (isAuthenticated || flagIsSet(RELOGIN_KEY)) {
      setNoAccess(true);
      return;
    }
    setFlag(RELOGIN_KEY);
    startLogin();
  }, [isAuthenticated, startLogin]);

  /**
   * A token that will not mint is a sign-in problem, never an account one: the
   * request never reached the server. The usual cause is Auth0 refusing the
   * stored refresh token (a 403 `invalid_grant` from `/oauth/token` — expired,
   * revoked, or rotated out by another tab), and `isAuthenticated` stays true
   * because the SDK still holds the old ID token. One trip through Auth0 mints
   * a fresh pair against the live Google session; `RELOGIN_KEY` holds that
   * "one" across the page load it causes, and a second failure is shown as what
   * it is rather than as a missing account.
   */
  const handleTokenFailure = useCallback(
    (cause: unknown) => {
      if (!flagIsSet(RELOGIN_KEY)) {
        setFlag(RELOGIN_KEY);
        startLogin();
        return;
      }
      setTokenError(cause instanceof Error ? cause.message : 'The session could not be renewed.');
    },
    [startLogin],
  );

  useEffect(() => {
    // A reader who just signed out is not an unauthenticated reader to be sent
    // back to Google — the Google session is still live and would return them
    // instantly. They get a screen and a button instead.
    if (signedOut) return;
    if (isLoading || isAuthenticated || error) return;
    startLogin();
  }, [signedOut, isLoading, isAuthenticated, error, startLogin]);

  const fetchWithAuth = useCallback(
    async (input: string, init?: RequestInit) => {
      const headers = new Headers(init?.headers);

      let token: string;
      try {
        token = await getAccessTokenSilently();
      } catch (cause) {
        // The synthetic response keeps the caller on its ordinary
        // error-envelope path instead of throwing something else.
        handleTokenFailure(cause);
        return new Response(null, { status: 401, statusText: 'Not signed in' });
      }

      headers.set('authorization', `Bearer ${token}`);
      const response = await fetch(input, { ...init, headers });
      // ui-design §6.3: 401 → sign in again. The server's rejections are plain
      // 401s, so this is the whole test.
      if (response.status === 401) handleUnauthorized();
      // Anything else is the session working end to end, which retires the
      // one-shot: a later visit gets its own attempt.
      else clearFlag(RELOGIN_KEY);
      return response;
    },
    [getAccessTokenSilently, handleUnauthorized, handleTokenFailure],
  );

  const signOut = useCallback(() => {
    // Set BEFORE the redirect: this is the only moment the app is still running
    // and knows the departure was deliberate.
    setFlag(SIGNED_OUT_KEY);
    void logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  /** The one explicit sign-in: both guards are retired by the reader asking. */
  const signInAgain = useCallback(() => {
    clearFlag(SIGNED_OUT_KEY);
    clearFlag(RELOGIN_KEY);
    setSignedOut(false);
    setNoAccess(false);
    setTokenError(null);
    redirecting.current = false;
    startLogin();
  }, [startLogin]);

  const value = useMemo<AuthState>(
    () => ({
      mode: 'auth0',
      fetchWithAuth,
      // The dev switcher's two identities have no meaning here; DevSwitch
      // renders nothing outside dev mode.
      devUser: null,
      devUsers: [],
      switchDevUser: () => {},
      signOut,
    }),
    [fetchWithAuth, signOut],
  );

  if (signedOut) {
    return (
      <BareState>
        <div className="line">You have been signed out.</div>
        <button type="button" className="quiet-action" onClick={signInAgain}>
          Sign in
        </button>
      </BareState>
    );
  }

  if (error || tokenError) {
    return (
      <BareState>
        <div className="line">Sign-in did not complete.</div>
        <div className="quiet">{error?.message ?? tokenError}</div>
        <button type="button" className="quiet-action" onClick={signInAgain}>
          Try again
        </button>
      </BareState>
    );
  }

  if (noAccess) {
    return (
      <BareState>
        <div className="line">
          You are signed in as {user?.email ?? 'this Google account'}, but this Google account has
          no access yet.
        </div>
        <div className="quiet">Ask an owner to add you, or sign in with another account.</div>
        <button type="button" className="quiet-action" onClick={signOut}>
          Sign out
        </button>
      </BareState>
    );
  }

  // The same screen `Bootstrap` shows while `/me` is pending, so sign-in, the
  // Auth0 round trip and the first read run together as one loading state.
  if (leaving || isLoading || !isAuthenticated) {
    return (
      <BareState>
        <div className="line">
          <Loading immediate />
        </div>
      </BareState>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Authorization Code + PKCE (ui-design §6.3). `useRefreshTokens` with the
 * client's rotating refresh tokens (`modules/auth0/spa.tf`) and a localstorage
 * cache are what make a hard refresh keep the session.
 *
 * `connection` names the tenant's only connection, which skips Auth0's
 * choose-a-method screen and goes straight to Google.
 */
function Auth0AuthProvider({ children }: { children: ReactNode }) {
  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        audience: AUTH0_AUDIENCE,
        redirect_uri: `${window.location.origin}${CALLBACK_PATH}`,
        connection: AUTH0_CONNECTION,
      }}
      useRefreshTokens
      cacheLocation="localstorage"
      onRedirectCallback={onRedirectCallback}
    >
      <Auth0Session>{children}</Auth0Session>
    </Auth0Provider>
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (AUTH_MODE === 'auth0') return <Auth0AuthProvider>{children}</Auth0AuthProvider>;
  return <DevAuthProvider>{children}</DevAuthProvider>;
}
